import { createStore, Store } from "redux";
import { createScene, TControlID, TWireID } from "../Scene";
import { TScene } from "../_generated/NodeTypes";

export type THoverState = {
  type: "control";
  controlId: TControlID;
};

export type TDragState =
  | {
      type: "move_wiring_control";
      controlId: TControlID;
    }
  | {
      type: "drag_select";
      start: [number, number];
    };

export type THoldingState = {
  type: "edit_wire";
  existingId?: TWireID;
  holdingSide: "start" | "end";
  connectedControl: TControlID;
};

type TActiveContextMenu = (
  | {
      type: "background";
      positionInScene: [number, number];
    }
  | {
      type: "control";
      controlId: TControlID;
    }
) & {
  position: [number, number];
};

type TSelection = {
  controls: TControlID[];
};

type TSceneUpdate = {
  type: "create_node";
  parentId: TControlID;
};

export type State = {
  scene: TScene;
  loadingScene?: TScene;

  hoverState: THoverState | null;
  dragState: TDragState | null;
  holdingState: THoldingState | null;

  selection: TSelection | null;
  clipboard: TSelection | null;

  canvasTranslate: [number, number];
  canvasZoom: number;

  appMousePosition: [number, number];

  activeContextMenu: TActiveContextMenu | null;

  // actionHistory: TSceneUpdate;

  serverSocket: WebSocket;
  roomMemberId: number;
};

export type DerivedState = {
  // cursor: TCursor | null;

  canvasMousePosition: [number, number];
  sceneMousePosition: [number, number];
};

export type ReadableState = State & DerivedState;
export type ReadableStateKey = keyof ReadableState;

const DEFAULT_VALUES: ReadableState = {
  scene: createScene(),

  hoverState: null,
  dragState: null,
  holdingState: null,

  selection: null,
  clipboard: null,

  canvasTranslate: [0, 0],
  canvasZoom: 1,

  appMousePosition: [0, 0],

  activeContextMenu: null,

  serverSocket: new WebSocket(""),
  roomMemberId: 0,

  // Derived

  // cursor: null,

  // sceneToDrawList: [],
  // sceneToDrawMap: {},
  // sceneToDrawMapTransformed: {},

  canvasMousePosition: [0, 0],
  sceneMousePosition: [0, 0],
};

export const appToCanvasPosition = ([x, y]: [number, number]): [
  number,
  number
] => [x, y];

export const canvasToAppPosition = ([x, y]: [number, number]): [
  number,
  number
] => [x, y];

export const canvasToScenePosition = (
  {
    canvasTranslate: [translateX, translateY],
    canvasZoom,
  }: Pick<State, "canvasTranslate" | "canvasZoom">,
  [x, y]: [number, number]
): [number, number] => [
  (x - translateX) / canvasZoom,
  (y - translateY) / canvasZoom,
];

export const sceneToCanvasPosition = (
  {
    canvasTranslate: [translateX, translateY],
    canvasZoom,
  }: Pick<State, "canvasTranslate" | "canvasZoom">,
  [x, y]: [number, number]
): [number, number] => [
  x * canvasZoom + translateX,
  y * canvasZoom + translateY,
];

export type SetAction<T extends keyof State> = {
  type: "set";
  key: T;
  value: State[T];
};

export type UpdateAction<T extends keyof State> = {
  type: "update";
  key: T;
  updater: (current: State[T]) => State[T];
};

type SetActionUnion<U extends keyof State> = U extends any
  ? SetAction<U>
  : never;

type UpdateActionUnion<U extends keyof State> = U extends any
  ? UpdateAction<U>
  : never;

type AllActions = SetActionUnion<keyof State> | UpdateActionUnion<keyof State>;

type DerivedStateReducer<Deps extends ReadableStateKey[]> = {
  // reducer: (args: {
  //   [K in Deps[number]]: ReadableState[K];
  // }) => Partial<ReadableState>;
  reducer: (args: ReadableState) => Omit<Partial<ReadableState>, Deps[number]>;
  deps: Deps;
};

const createDerivedStateReducer = <Deps extends ReadableStateKey[]>(
  reducer: DerivedStateReducer<Deps>["reducer"],
  deps: Deps
): DerivedStateReducer<Deps> => ({
  reducer,
  deps,
});

const derivedStateReducers = [
  // createDerivedStateReducer(
  //   ({ scene }) => {
  //     // const yogaNode = toYogaNode(scene, scene.nodes[scene.rootNode]);
  //     // yogaNode.calculateLayout();
  //     // const toDraw = extractBoundsAndDrawOrder(scene, yogaNode);
  //     validateScene(scene);

  //     const rootNodes = scene.rootNodes.map((id) => getNode(scene, id));

  //     const drawIdToYogaNode = {}; // Mutable
  //     const rootYogaNodes = rootNodes.map(
  //       (node) => toYogaNodes(scene, node, drawIdToYogaNode)[0]
  //     );

  //     // const logYogaNode = (node: (typeof rootYogaNodes)[number], depth = 0) => {
  //     //   console.log(
  //     //     depth + "\t".repeat(depth),
  //     //     node,
  //     //     Object.entries(drawIdToYogaNode).find(
  //     //       // @ts-expect-error
  //     //       ([, v]) => v.__nbindPtr === node.__nbindPtr
  //     //     )
  //     //   );
  //     //   const numChildren = node.getChildCount();
  //     //   for (let i = 0; i < numChildren; i++) {
  //     //     logYogaNode(node.getChild(i), depth + 1);
  //     //   }
  //     //   console.log("\n");
  //     // };
  //     // logYogaNode(rootYogaNodes[0]);

  //     rootYogaNodes.forEach((node) => node.calculateLayout());
  //     const toDraw = rootNodes
  //       .map((node) =>
  //         extractBoundsAndDrawOrder(scene, node.id, drawIdToYogaNode)
  //       )
  //       .flat();

  //     return {
  //       sceneToDrawList: toDraw,
  //       sceneToDrawMap: Object.fromEntries(toDraw),
  //     };
  //   },
  //   ["scene"]
  // ),

  // createDerivedStateReducer(
  //   ({ scene, dragState, hoverState, selection, scopedNode }) => ({
  //     dragState:
  //       ((dragState?.type === "node" ||
  //         dragState?.type === "surface" ||
  //         dragState?.type === "resize") &&
  //         getNode(scene, dragState.nodeId) === undefined) ||
  //       (dragState?.type === "move_wiring_control" &&
  //         getControl(scene.wiring, dragState.controlId) === undefined)
  //         ? null
  //         : dragState,
  //     hoverState:
  //       ((hoverState?.type === "node" ||
  //         hoverState?.type === "node_drop_area" ||
  //         hoverState?.type === "property") &&
  //         getNode(scene, hoverState.nodeId) === undefined) ||
  //       (hoverState?.type === "control" &&
  //         getControl(scene.wiring, hoverState.controlId) === undefined)
  //         ? null
  //         : hoverState,
  //     selection:
  //       selection?.type === "node"
  //         ? {
  //             type: "node",
  //             nodes: selection.nodes.filter(
  //               (v) => getNode(scene, v) !== undefined
  //             ),
  //           }
  //         : selection?.type === "control"
  //         ? {
  //             type: "control",
  //             controls: selection.controls.filter(
  //               (v) => getControl(scene.wiring, v) !== undefined
  //             ),
  //           }
  //         : selection,
  //     scopedNode:
  //       scopedNode !== null && getNode(scene, scopedNode) === undefined
  //         ? null
  //         : scopedNode,
  //   }),
  //   ["scene"]
  // ),

  // createDerivedStateReducer(
  //   ({
  //     sceneToDrawMap,
  //     canvasTranslate: [translateX, translateY],
  //     canvasZoom,
  //   }) => ({
  //     sceneToDrawMapTransformed: updateEntries(
  //       sceneToDrawMap,
  //       ([id, layout]) => {
  //         const scaled = updateEntries(layout, ([k, v]) => [k, v * canvasZoom]);
  //         return [
  //           id,
  //           {
  //             ...scaled,
  //             left: scaled.left + translateX,
  //             right: scaled.right + translateX,
  //             top: scaled.top + translateY,
  //             bottom: scaled.bottom + translateY,
  //           },
  //         ];
  //       }
  //     ),
  //   }),
  //   ["sceneToDrawMap", "canvasTranslate", "canvasZoom"]
  // ),

  // createDerivedStateReducer(
  //   ({ activeTool, dragState }) => ({
  //     cursor:
  //       dragState?.type === "drag_select"
  //         ? "crosshair"
  //         : dragState?.type === "node" ||
  //           dragState?.type === "surface" ||
  //           dragState?.type === "move_wiring_control"
  //         ? "move"
  //         : dragState?.type === "resize"
  //         ? dragState.side === "width"
  //           ? "ew-resize"
  //           : dragState.side === "height"
  //           ? "ns-resize"
  //           : "nwse-resize"
  //         : dragState?.type === "draw_layer_reorder"
  //         ? "grabbing"
  //         : dragState?.type === "function_area_resize"
  //         ? "nwse-resize"
  //         : activeTool !== null
  //         ? "crosshair"
  //         : null,
  //   }),
  //   ["activeTool", "dragState"]
  // ),

  // createDerivedStateReducer(
  //   ({ appMousePosition: [appX, appY], explorerWidth }) => ({
  //     canvasMousePosition: [appX - explorerWidth, appY - TOOLBAR_HEIGHT],
  //   }),
  //   ["appMousePosition", "explorerWidth"]
  // ),
  createDerivedStateReducer(
    ({ appMousePosition }) => ({
      canvasMousePosition: appMousePosition,
    }),
    ["appMousePosition"]
  ),

  createDerivedStateReducer(
    ({
      canvasMousePosition: [canvasX, canvasY],
      canvasTranslate: [translateX, translateY],
      canvasZoom,
    }) => ({
      sceneMousePosition: [
        (canvasX - translateX) / canvasZoom,
        (canvasY - translateY) / canvasZoom,
      ],
    }),
    ["canvasMousePosition", "canvasTranslate", "canvasZoom"]
  ),

  // createDerivedStateReducer(
  //   ({
  //     dragState,
  //     canvasMousePosition: [endX, endY],
  //     scene,
  //     scopedNode,
  //     sceneToDrawMapTransformed,
  //   }) => {
  //     // return {};
  //     if (dragState?.type !== "drag_select") {
  //       return {};
  //     }
  //     return {
  //       selection: {
  //         type: "node",
  //         nodes: keys(scene.nodes).filter(
  //           (nodeId) =>
  //             (scopedNode === null ||
  //               isAncestorOf(scene, scopedNode, nodeId)) &&
  //             // nodeId !== scopedNode &&
  //             !ancestorHasCondition(
  //               scene,
  //               nodeId,
  //               (node) => node.isLocked || node.isHidden
  //             ) &&
  //             scene.nodes[nodeId].type !== "surface" &&
  //             layoutIntersectsBounds(
  //               sceneToDrawMapTransformed[nodeId],
  //               [
  //                 Math.min(dragState.start[0], endX),
  //                 Math.min(dragState.start[1], endY),
  //               ],
  //               [
  //                 Math.abs(dragState.start[0] - endX),
  //                 Math.abs(dragState.start[1] - endY),
  //               ]
  //             )
  //         ),
  //       },
  //     };
  //   },
  //   [
  //     "dragState",
  //     "canvasMousePosition",
  //     "scene",
  //     "scopedNode",
  //     "sceneToDrawMapTransformed",
  //   ]
  // ),
] as const;

const sharedStateReducer = (
  state: ReadableState = DEFAULT_VALUES,
  action: AllActions
): ReadableState => {
  let newState = {
    ...state,
    [action.key]:
      action.type === "update"
        ? (action as UpdateAction<typeof action.key>).updater(state[action.key])
        : action.value,
  };

  const derivedReducerStack: (typeof derivedStateReducers)[number][] = [];
  const queueDependentReducers = (key: ReadableStateKey) => {
    for (const reducer of derivedStateReducers) {
      if ((reducer.deps as ReadableStateKey[]).includes(key)) {
        derivedReducerStack.push(reducer);
      }
    }
  };

  queueDependentReducers(action.key);
  while (derivedReducerStack.length > 0) {
    const reducer = derivedReducerStack.pop()!;
    const toAppend = reducer.reducer(newState);
    for (const key of Object.keys(toAppend) as ReadableStateKey[]) {
      queueDependentReducers(key);
    }
    newState = { ...newState, ...toAppend };
  }

  return newState;
};

export const editorStore: Store<
  ReadableState,
  SetActionUnion<keyof State> | UpdateActionUnion<keyof State>
> = createStore(
  sharedStateReducer,
  // sharedStateReducer(DEFAULT_VALUES, {
  //   type: "set",
  //   key: "scene",
  //   // value: SAVE_LOAD_SCENE,
  //   value: SAVE_LOAD_SCENE,
  // }),
  undefined,
  (window as any).__REDUX_DEVTOOLS_EXTENSION__ &&
    (window as any).__REDUX_DEVTOOLS_EXTENSION__()
);

//
//
// Boilerplate

// import { configureStore, ThunkAction, Action } from "@reduxjs/toolkit";

// export const store = configureStore({
//   reducer: {},
// });

export type AppDispatch = typeof editorStore.dispatch;
export type RootState = ReturnType<typeof editorStore.getState>;
// export type AppThunk<ReturnType = void> = ThunkAction<
//   ReturnType,
//   RootState,
//   unknown,
//   Action<string>
// >;
